import { useState } from "react";
import type { MfgMsdsStatus } from "@/lib/manufacturing/msdsStatus";
import {
  msdsStatusFromSuggestion,
  sdsSuggestionLabel,
  type SdsClassificationResult,
} from "@/lib/manufacturing/msdsClassify";

const OPTIONS: ReadonlyArray<{ value: MfgMsdsStatus; label: string }> = [
  { value: "not_required", label: "Not required" },
  { value: "required", label: "Required" },
  { value: "undecided", label: "Decide later" },
];

type Props = {
  value: MfgMsdsStatus;
  onChange: (next: MfgMsdsStatus) => void;
  classification: SdsClassificationResult | null;
  classifyLoading: boolean;
  classifyError: string | null;
  /** True when name+category are ready for classify. */
  classifyReady: boolean;
};

/** MSDS-1/2 — SDS requirement radios + AI triage strip. */
export function MaterialModalMsdsField({
  value,
  onChange,
  classification,
  classifyLoading,
  classifyError,
  classifyReady,
}: Props) {
  const [showWhy, setShowWhy] = useState(false);

  return (
    <fieldset className="block rounded-lg border border-gray-200 p-3 text-xs text-gray-600 dark:border-zinc-700 dark:text-zinc-300">
      <legend className="px-1 text-xs font-medium text-gray-700 dark:text-zinc-200">
        Safety Data Sheet (SDS)
      </legend>

      {classifyReady ? (
        <div className="mb-2 rounded-md bg-sky-50 px-2 py-1.5 text-[11px] text-sky-950 dark:bg-sky-950/40 dark:text-sky-100">
          {classifyLoading ? (
            <span>AI suggestion: checking…</span>
          ) : classifyError ? (
            <span>AI suggestion unavailable — choose manually.</span>
          ) : classification ? (
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium">
                  AI suggestion: {sdsSuggestionLabel(classification.suggestion)}
                </span>
                <span className="text-sky-800/80 dark:text-sky-200/80">
                  ({classification.confidence} confidence)
                </span>
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => setShowWhy((v) => !v)}
                >
                  {showWhy ? "Hide why" : "Why?"}
                </button>
                <button
                  type="button"
                  className="rounded border border-sky-300 bg-white px-1.5 py-0.5 font-medium text-sky-900 hover:bg-sky-100 dark:border-sky-700 dark:bg-zinc-900 dark:text-sky-100 dark:hover:bg-zinc-800"
                  onClick={() => onChange(msdsStatusFromSuggestion(classification.suggestion))}
                >
                  Apply suggestion
                </button>
              </div>
              {showWhy ? (
                <p className="text-sky-900/90 dark:text-sky-100/90">{classification.reasoning}</p>
              ) : null}
            </div>
          ) : (
            <span>AI suggestion: waiting…</span>
          )}
        </div>
      ) : (
        <p className="mb-2 text-[11px] text-gray-500 dark:text-zinc-400">
          Enter name and category to get an AI SDS suggestion.
        </p>
      )}

      <div className="mb-1 text-[11px] text-gray-600 dark:text-zinc-300">
        Does this material need an SDS in your plant?
      </div>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:gap-3">
        {OPTIONS.map(({ value: option, label }) => (
          <label key={option} className="inline-flex items-center gap-1.5 font-normal">
            <input
              type="radio"
              name="msds_status"
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
